import { getMonthWeekBuckets, toISODate } from '@/client/lib/dates';
import { isEntryLocked, isRangeCovered } from '@/client/lib/timesheets';
import type { RouterOutputs } from '@/client/utils/trpc';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { DayCard } from './DayCard';

type Entry = RouterOutputs['timesheets']['listEntries'][number];
type Submission = RouterOutputs['timesheets']['listSubmissions'][number];

interface MonthViewProps {
  year: number;
  month: number;
  entries: Entry[];
  submissions: Submission[];
  onAdd?: (date: string) => void;
  onEdit?: (entry: Entry) => void;
  readOnly?: boolean;
}

function getDaysInRange(from: string, to: string): string[] {
  const days: string[] = [];
  const cursor = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  while (cursor <= end) {
    days.push(toISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function noop() {}

export function MonthView({
  year,
  month,
  entries,
  submissions,
  onAdd,
  onEdit,
  readOnly = false,
}: MonthViewProps) {
  const buckets = getMonthWeekBuckets(year, month);
  const today = toISODate(new Date());

  const [openBuckets, setOpenBuckets] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    buckets.forEach((b, i) => {
      if (today >= b.from && today <= b.to) initial.add(i);
    });
    return initial;
  });

  function toggleBucket(i: number) {
    setOpenBuckets((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-2">
      {buckets.map((bucket, i) => {
        const bucketEntries = entries.filter(
          (e) => e.date >= bucket.from && e.date <= bucket.to,
        );
        const totalHours = bucketEntries.reduce((s, e) => s + e.hours, 0);
        const isSubmitted = isRangeCovered(bucket.from, bucket.to, submissions);
        const isOpen = openBuckets.has(i);
        const days = getDaysInRange(bucket.from, bucket.to);

        return (
          <div key={bucket.from} className="overflow-hidden rounded-xl border border-border/70 bg-card shadow-soft">
            <button
              type="button"
              className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-muted/40"
              onClick={() => toggleBucket(i)}
            >
              {isOpen ? (
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <span className="flex-1 text-sm font-semibold">{bucket.label}</span>
              <span className="tabular-nums text-sm text-muted-foreground">
                {totalHours > 0 ? `${totalHours.toFixed(1)}h` : '—'}
              </span>
              {isSubmitted && (
                <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
                  Submitted
                </span>
              )}
            </button>
            {isOpen && (
              <div className="flex flex-col gap-3 border-t p-4">
                {days.map((date) => (
                  <DayCard
                    key={date}
                    date={date}
                    entries={entries.filter((e) => e.date === date)}
                    isLocked={readOnly || isEntryLocked(date, submissions)}
                    onAdd={onAdd ?? noop}
                    onEdit={onEdit ?? noop}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
